/** biome-ignore-all lint/correctness/useUniqueElementIds: single-instance form, same as the self-hosted auth components */
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageMeta } from '@/components/page-meta';
import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { apiHostnameManager } from '@/utils/api-hostname-manager';
import { getApiHostnameCandidates } from '@/utils/local-api-fallback';
import { setJwtToken } from '@/utils/self-hosted/jwt-manager';

/** Mirrors `passwordConstraints` in @novu/shared; the API enforces the same rule server-side. */
const RULES = [
  { label: 'At least 8 characters', test: (value: string) => value.length >= 8 && value.length <= 64 },
  { label: 'An uppercase letter', test: (value: string) => /[A-Z]/.test(value) },
  { label: 'A lowercase letter', test: (value: string) => /[a-z]/.test(value) },
  { label: 'A number', test: (value: string) => /[0-9]/.test(value) },
  { label: 'A special character', test: (value: string) => /[#?!@$%^&*()-]/.test(value) },
];

async function postAcceptInvitation(body: { token: string; password: string }): Promise<Response> {
  const candidates = getApiHostnameCandidates(apiHostnameManager.getHostname());
  let lastError: unknown;

  for (const baseUrl of candidates) {
    try {
      return await fetch(`${baseUrl}/v1/auth/accept-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error('Could not reach the API');
}

/**
 * Set a first password from a tenant invitation.
 *
 * The invitee has no session, so this page is public and the token in the URL
 * is the credential. On success the API returns a normal user token, which is
 * stored here so they land in their own workspace signed in -- not back at the
 * login form typing a password they just chose.
 */
export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const unmet = useMemo(() => RULES.filter((rule) => !rule.test(password)), [password]);
  const canSubmit = token !== '' && unmet.length === 0 && password === confirmation && !isSubmitting;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await postAcceptInvitation({ token, password });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'This invitation link is invalid or has expired.');
      }

      const issued = data?.data?.token ?? data?.token;

      if (!issued) {
        throw new Error('No session was returned. Please try signing in instead.');
      }

      setJwtToken(issued);
      (window as unknown as { Clerk?: Record<string, unknown> }).Clerk = {
        ...((window as unknown as { Clerk?: Record<string, unknown> }).Clerk || {}),
        loggedIn: true,
      };
      navigate('/');
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto w-full max-w-md pt-16 text-center">
        <PageMeta title="Invitation" />
        <h2 className="text-title-h5 text-text-strong mb-2">This link is incomplete</h2>
        <p className="text-paragraph-sm text-text-soft">Ask your administrator to send the invitation again.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-md pt-12">
      <PageMeta title="Set your password" />
      <h2 className="text-title-h5 text-text-strong mb-1 text-center">Set your password</h2>
      <p className="text-paragraph-sm text-text-soft mb-6 text-center">
        Choose a password to finish setting up your pingr-notify account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="password" className="text-label-sm text-text-strong mb-1 block">
            Password
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            required
            className="w-full"
          />
        </div>

        <ul className="flex flex-col gap-1">
          {RULES.map((rule) => {
            const met = rule.test(password);

            return (
              <li
                key={rule.label}
                className={`text-paragraph-xs flex items-center gap-2 ${met ? 'text-success-base' : 'text-text-soft'}`}
              >
                <span aria-hidden>{met ? '✓' : '○'}</span>
                {rule.label}
              </li>
            );
          })}
        </ul>

        <div>
          <label htmlFor="confirmation" className="text-label-sm text-text-strong mb-1 block">
            Confirm password
          </label>
          <Input
            id="confirmation"
            type="password"
            value={confirmation}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmation(e.target.value)}
            required
            className="w-full"
          />
          {confirmation !== '' && confirmation !== password && (
            <p className="text-paragraph-xs text-error-base mt-1">The two passwords do not match.</p>
          )}
        </div>

        {error && <p className="text-paragraph-sm text-error-base">{error}</p>}

        <Button type="submit" variant="primary" mode="filled" className="w-full" disabled={!canSubmit}>
          {isSubmitting ? 'Setting your password...' : 'Set password and continue'}
        </Button>
      </form>
    </div>
  );
}

export default AcceptInvitationPage;
